
import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import {
  getPlanDetails,
  createPlanDetails,
  updatePlanDetails,
  deletePlanDetails,
} from "../api/auth/planService";
import { getAllPlanTypes } from "../api/auth/planTypeService";

function Plan() {
  // -------------------- STATES --------------------
  const [planDetails, setPlanDetails] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [featureInput, setFeatureInput] = useState(""); // Input for feature
  const [values, setValues] = useState({
    id: "",
    plantype_id: "",
    price: "",
    features: [],
    details: "",
    date: "",
  });

  // -------------------- FETCH DATA --------------------

  useEffect(() => {
    fetchPlanTypes();
    fetchPlanDetails();
  }, []);

  const fetchPlanTypes = async () => {
    try {
      const planTypesData = await getAllPlanTypes();
      setPlanTypes(planTypesData);
    } catch (error) {

      console.error("Error fetching plan types:", error);
      Swal.fire("Error!", "Failed to fetch plan types.", "error");

    }
  };

  const fetchPlanDetails = async () => {
    try {
      const planData = await getPlanDetails();
      setPlanDetails(planData);
    } catch (error) {

      console.error("Error fetching plan details:", error);
      Swal.fire("Error!", "Failed to fetch plan details.", "error");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddFeature = () => {
    if (featureInput.trim() !== "") {
      setValues((prev) => ({
        ...prev,
        features: [...prev.features, featureInput.trim()],
      }));
      setFeatureInput("");
    }
  };

  const handleRemoveFeature = (index) => {
    setValues((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const plantypeId = parseInt(values.plantype_id, 10);
    if (
      isNaN(plantypeId) ||
      plantypeId <= 0 ||
      !planTypes.some((type) => type.id === plantypeId)
    ) {
      Swal.fire("Error!", "Please select a valid plan type.", "error");
      return;
    }

    const currentDate = new Date().toISOString().split("T")[0];
    const payload = {
      plantypeId: plantypeId,
      price: values.price,
      feature: values.features.join(", "),
      details: values.details,
      date: currentDate,
    };

    try {
      if (values.id) {
        await updatePlanDetails(values.id, payload);
        Swal.fire("Success!", "Plan updated successfully", "success");
      } else {
        await createPlanDetails(payload);
        Swal.fire("Success!", "Plan added successfully", "success");

      }

      // Reset form
      setValues({

        id: "",
        plantype_id: "",
        price: "",
        features: [],
        details: "",
        date: "",
      });
      setFeatureInput("");
      fetchPlanDetails();
    } catch (error) {
      console.error("Error submitting plan:", error);
      Swal.fire("Error!", error.message || "Plan submit failed", "error");

    }
  };

  const handleDelete = (id) => {
    Swal.fire({

      title: "Are you sure?",
      text: "You will not be able to recover this entry!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",

    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deletePlanDetails(id);

          setPlanDetails((prev) => prev.filter((item) => item.id !== id));
          Swal.fire("Deleted!", "The plan entry has been deleted.", "success");
        } catch (error) {
          Swal.fire("Error!", "There was a problem deleting the entry.", "error");
          console.error("Error deleting plan:", error);

        }
      }
    });
  };

  const handleEdit = (item) => {
    setValues({
      id: item.id,

      plantype_id: item.plantype?.id ? String(item.plantype.id) : "",
      price: item.price || "",
      features: item.feature ? item.feature.split(",").map((f) => f.trim()) : [],
      details: item.details || "",
      date: item.date || "",
    });
  };

  return (
    <div>
      <Sidebar />
      <div className="col-md-12 px-0" style={{ marginLeft: "210px" }}>
        <div className="container-fluid">
          {/* Page Title */}

          <div className="row">
            <div className="col-md-12">
              <h5 className="page-title mt-3">Clinic Solution Update / Plan</h5>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-md-8">
              <form onSubmit={handleSubmit}>
                <div className="row">

                  {/* Plan Type */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Plan Type</label>

                    <select
                      className="form-select"
                      name="plantype_id"
                      value={values.plantype_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Plan Type</option>

                      {planTypes.map((type) => (

                        <option key={type.id} value={type.id}>
                          {type.plantype}
                        </option>
                      ))}
                    </select>
                  </div>


                  <div className="col-md-2 mb-3">
                    <label className="form-label">Price</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Price"

                      name="price"
                      value={values.price}
                      onChange={handleChange}
                      required
                    />
                  </div>


                  <div className="col-md-6 mb-3">
                    <label className="form-label">Features</label>

                    <div className="d-flex">
                      <input
                        type="text"
                        className="form-control"
                        value={featureInput}
                        onChange={(e) => setFeatureInput(e.target.value)}
                        placeholder="Enter a feature"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddFeature();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary ms-2"
                        onClick={handleAddFeature}
                      >
                        <i className="fa fa-plus me-1"></i>
                      </button>
                    </div>
                  </div>

                  {/* Features Table */}
                  <table className="table table-bordered table-sm mb-2 ml-3">
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: "50%" }}>Feature</th>
                        <th style={{ width: "1%" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {values.features.length === 0 ? (
                        <tr>
                          <td colSpan="2" className="text-center text-muted">
                            No features added
                          </td>
                        </tr>
                      ) : (
                        values.features.map((feature, index) => (
                          <tr key={index}>
                            <td className="align-middle">
                              <i className="fa fa-check-circle text-success me-2"></i>
                              {feature}
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => handleRemoveFeature(index)}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {/* Details */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Details</label>
                    <textarea
                      className="form-control"
                      name="details"
                      value={values.details}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Enter detailed description..."
                      required
                    ></textarea>

                  </div>
                </div>

                <button type="submit" className="btn btn-primary">

                  {values.id ? "Update" : "Save"}

                </button>
              </form>
            </div>
          </div>


          {/* -------------------- PLAN DETAILS TABLE -------------------- */}
          <div className="row mt-4">
            <div className="col-md-10">
              <table className="table table-bordered table-striped">
                <thead className="table-dark">

                  <tr>
                    <th>#</th>
                    <th>Plan Type</th>
                    <th>Price</th>

                    <th>Features</th>
                    <th>Details</th>
                    <th>Date</th>
                    <th style={{ width: "150px" }}>Action</th>

                  </tr>
                </thead>
                <tbody>
                  {planDetails.length === 0 ? (
                    <tr>

                      <td colSpan="7" className="text-center">
                        No Plan Details data available.
                      </td>
                    </tr>
                  ) : (
                    planDetails.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.plantype || "N/A"}</td>
                        <td>{item.price}</td>
                        <td>
                          <ul className="mb-0 ps-3">
                            {item.feature &&
                              item.feature.split(",").map((f, i) => (
                                <li key={i}>
                                  <i className="fa fa-check text-success me-2"></i>
                                  {f.trim()}
                                </li>
                              ))}
                          </ul>
                        </td>
                        <td>{item.details}</td>
                        <td>{item.date || "N/A"}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => handleEdit(item)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"

                            onClick={() => handleDelete(item.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Plan;

